export class StorageManager {
  constructor() {
    this.TOKEN_KEY = 'auth_token';
    this.TOKEN_DATA_KEY = 'auth_token_data';
    this.VERSION_KEY = 'auth_version';
    this.CURRENT_VERSION = '1.0'; // Current version of the auth system
  }

  // Helper function to set a cookie with security options
  setCookie(name, value, days = 7, options = {}) {
    console.log('[REFRESH-DEBUG] Setting cookie:', name);
    console.log('[REFRESH-DEBUG] Current cookies before setting:', document.cookie);
    
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    
    // Default options for secure cookies
    // Note: HttpOnly cookies can only be set by the server, not by client-side JavaScript
    const defaultOptions = {
      path: '/',
      sameSite: 'lax', // Changed from 'strict' to 'lax' to allow cookies to persist on page transitions
      secure: window.location.protocol === 'https:'
    };
    
    const cookieOptions = { ...defaultOptions, ...options };
    console.log('[REFRESH-DEBUG] Cookie options:', cookieOptions);
    
    let cookieString = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}`;
    
    // Add all options to cookie string
    Object.entries(cookieOptions).forEach(([key, value]) => {
      if (value === true) {
        cookieString += `; ${key}`;
      } else if (value !== false) {
        cookieString += `; ${key}=${value}`;
      }
    });
    
    console.log('[REFRESH-DEBUG] Setting cookie with string:', cookieString);
    document.cookie = cookieString;
    
    // Verify the cookie was set
    const verifySet = this.getCookie(name);
    console.log('[REFRESH-DEBUG] Verification - Cookie set successfully:', !!verifySet);
    console.log('[REFRESH-DEBUG] Cookies after setting:', document.cookie);
    
    return cookieString;
  }

  // Helper function to get a cookie by name
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
  }

  // Helper function to delete a cookie
  deleteCookie(name) {
    this.setCookie(name, '', -1);
  }

  async storeToken(tokenData) {
    try {
      console.log('Storing token data:', tokenData);
      
      const dataToStore = {
        ...tokenData,
        timestamp: new Date().toISOString(),
        version: this.CURRENT_VERSION
      };
      
      // Store the auth version separately to make it easier to check
      localStorage.setItem(this.VERSION_KEY, this.CURRENT_VERSION);
      
      // Store token in a secure cookie
      // For "remember me", use 365 days (1 year) for long-term persistence
      // For regular sessions, use 7 days to ensure reasonable persistence without requiring frequent logins
      const expirationDays = tokenData.rememberMe ? 365 : 7;
      this.setCookie(this.TOKEN_KEY, tokenData.token, expirationDays);
      
      // Store additional token data in localStorage for non-sensitive data
      // This is needed because cookies have size limitations
      localStorage.setItem(this.TOKEN_DATA_KEY, JSON.stringify(dataToStore));
      
      console.log('Token stored in cookie and additional data in localStorage');

      // Dispatch event for cross-tab sync
      const event = new CustomEvent('authStateChanged', {
        detail: {
          type: 'TOKEN_UPDATED',
          data: dataToStore
        }
      });
      
      window.dispatchEvent(event);
      console.log('Auth state change event dispatched');
      
      // Verify storage
      const storedToken = this.getCookie(this.TOKEN_KEY);
      if (!storedToken) {
        throw new Error('Token cookie verification failed');
      }
      console.log('Token storage verified');
      
    } catch (error) {
      console.error('Error storing token:', error);
      throw error;
    }
  }

  async getStoredToken() {
    try {
      console.log('[REFRESH-DEBUG] === Start getStoredToken ===');
      console.log('[REFRESH-DEBUG] Document cookie:', document.cookie);
      console.log('[REFRESH-DEBUG] Attempting to retrieve stored token');
      
      // Get token from cookie
      const token = this.getCookie(this.TOKEN_KEY);
      console.log('[REFRESH-DEBUG] Token cookie value:', token);
      
      if (!token) {
        console.log('[REFRESH-DEBUG] No token found in cookies');
        return null;
      }

      // Check auth version
      const storedVersion = localStorage.getItem(this.VERSION_KEY);
      console.log('[REFRESH-DEBUG] Stored auth version:', storedVersion || 'not found');
      
      // Get additional token data from localStorage
      const storedData = localStorage.getItem(this.TOKEN_DATA_KEY);
      console.log('[REFRESH-DEBUG] Additional token data exists:', !!storedData);
      
      if (!storedData) {
        console.log('[REFRESH-DEBUG] No token data found in localStorage, clearing token cookie');
        await this.clearToken();
        return null;
      }
      
      // If the version has changed, we can handle migration or decide to keep the session
      // For now, we'll keep the session even if the version changes, but log it
      if (storedVersion && storedVersion !== this.CURRENT_VERSION) {
        console.log(`[REFRESH-DEBUG] Auth version changed from ${storedVersion} to ${this.CURRENT_VERSION}, but keeping session`);
        // Update the version for future checks
        localStorage.setItem(this.VERSION_KEY, this.CURRENT_VERSION);
      }

      const tokenData = JSON.parse(storedData);
      console.log('[REFRESH-DEBUG] Parsed token data:', {
        hasToken: !!token,
        type: tokenData.type,
        hasStudent: !!tokenData.student,
        hasSchool: !!tokenData.school,
        expiresAt: tokenData.expiresAt,
        tokenFromCookie: token,
        tokenFromLocalStorage: tokenData.token,
        cookieMatchesStorage: token === tokenData.token
      });

      // Ensure the token in the cookie matches the one in localStorage
      if (token !== tokenData.token) {
        console.log('[REFRESH-DEBUG] Token mismatch between cookie and localStorage, clearing both');
        console.log('[REFRESH-DEBUG] Cookie token: ', token);
        console.log('[REFRESH-DEBUG] LocalStorage token: ', tokenData.token);
        await this.clearToken();
        return null;
      }

      // Check if token has expired
      if (tokenData.expiresAt) {
        const now = new Date();
        const expiresAt = new Date(tokenData.expiresAt);
        const isExpired = expiresAt < now;
        
        console.log('Token expiration check:', {
          now: now.toISOString(),
          expiresAt: expiresAt.toISOString(),
          isExpired
        });

        if (isExpired) {
          console.log('Token has expired, clearing storage');
          await this.clearToken();
          return null;
        }
      }

      // Return the complete token data with the token from the cookie
      const completeTokenData = {
        ...tokenData,
        token: token
      };

      console.log('Valid token retrieved from storage');
      return completeTokenData;
    } catch (error) {
      console.error('Error getting stored token:', error);
      // On any error, clear the potentially corrupted token
      await this.clearToken();
      return null;
    }
  }

  async clearToken(source = null) {
    try {
      // Clear token cookie
      this.deleteCookie(this.TOKEN_KEY);
      
      // Clear token data and version from localStorage
      localStorage.removeItem(this.TOKEN_DATA_KEY);
      localStorage.removeItem(this.VERSION_KEY);
      
      console.log('Token cleared from cookie and localStorage');
      
      // Dispatch event for cross-tab sync with optional source
      window.dispatchEvent(
        new CustomEvent('authStateChanged', {
          detail: {
            type: 'TOKEN_CLEARED',
            source: source // Include source in event detail
          }
        })
      );
    } catch (error) {
      console.error('Error clearing token:', error);
      throw error;
    }
  }
}